import './ApplyModal.scss';

import { Modal, Button, Form } from "react-bootstrap";
import { useState, useEffect } from "react";
import { api, setAuthToken } from '../../../utils/api';
import Brand from "../../../components/Brand";
import { faShare } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useCurrentUser } from "../../../UserProvider";
import classNames from "classnames";
import z from 'zod';
import { useForm } from '../../../hooks/Form';
import { Link } from 'react-router-dom';
import SsoButtons from "../../LoginPage/SsoButtons";

import SpotifyConnect from '../../OnboardingPage/OnboardingSteps/SpotifyConnect';

const schema = z.object({
  email: z.string()
    .nonempty("Email is required")
    .email("Invalid email address")
});

function createInitialValues() {
  return {
    email: '',
  };
}

function createInitialErrors() {
  return {
    email: null,
  };
}

export default function ApplyModal({show, handleClose, job}) {
  const { currentUser, refreshUser } = useCurrentUser();
  const [details, setDetails] = useState("");
  const [success, setSuccess] = useState(false);

  const { isLoading, setIsLoading, errors, setErrors, setFieldError, values, setFieldValue } = useForm({
    initialValues: createInitialValues(),
    initialErrors: createInitialErrors()
  });

  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    if (show) {
      setDetails("");
      setErrors(createInitialErrors());
      setFieldValue("email", "");
      setIsLoading(false);
      setSuccess(false);
    }
  }, [show]);
  /* eslint-enable react-hooks/exhaustive-deps */

  const handleApply = async (updatedData) => {
    // STEP 1 — ensure user exists / email captured
    if (!currentUser || !currentUser.registered) {
      try {
        setErrors(createInitialErrors());
        setIsLoading(true);

        const result = schema.safeParse(values);

        if (!result.success) {
          result.error.issues.forEach(err => {
            setFieldError(err.path[0], err.message);
          });
          return;
        }

        if (!currentUser) {
          const { data } = await api.post('/users', { email: values.email });
          setAuthToken(data);
        } else {
          await api.put('/users/me', { email: values.email });
        }
        await refreshUser();

      } catch (err) {
        if (err.response?.status === 409) {
          setFieldError('email', 'The email is already taken');
        } else {
          setFieldError('email', 'Something went wrong. Try again.');
        }

        setIsLoading(false);
        return;
      } finally {
        setIsLoading(false);
      }
    }

    // STEP 2 — apply to job (only runs if STEP 1 succeeded)
    try {
      await api.post('/users_job_postings', {
        job_posting_id: job.id,
        details
      });
      setSuccess(true);
    } catch (err) {
      console.error("Failed to apply:", err);
    }
  };

  if (success) {
    return (
      <Modal className="__apply-modal" show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Brand.Logo height={46} className="brand__logo" />
        </Modal.Header>

        <Modal.Body>
          <Modal.Title className="modal-title">Application Sent!</Modal.Title>
          <div className="modal-subtitle">
            The job poster will receive your profile shortly.
          </div>

          { currentUser && currentUser.profile && !currentUser.profile.completed &&
            <div className="complete-profile">
              <div className="modal-subtitle incomplete-profile">
                <>
                  We have noticed that your{" "}
                  <Link
                    to={`/a/${currentUser.profile.display_name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    profile
                  </Link>{" "}
                  is incomplete. Click{" "}
                  <Link
                    to={`/a/${currentUser.profile.display_name}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    here
                  </Link>{" "}
                  to build your profile so you have a better chance to get picked.
                </>
              </div>
              <div className="modal-subtitle">
                Alternatively, you can connect your Spotify profile to Papaya to fill in the blanks:
              </div>
              <SpotifyConnect handleNext={handleClose} />
            </div>
          }
          <div className='cta'>
            <Button className="ppy-btn ppy-btn-primary" onClick={handleClose}>
              Close
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    );
  }

  return (
    <Modal className="__apply-modal" show={show} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Brand.Logo height={ 46 } className="brand__logo" />
      </Modal.Header>

      <Modal.Body>
        <Modal.Title className='modal-title'>You're Almost There</Modal.Title>
        <div className="modal-subtitle">
          We'll make sure your application reaches the job poster.
        </div>
        <Form>
          { (!currentUser || !currentUser.registered) &&
            <div className="logged-out-controls">
              <div className='sso'>
                <SsoButtons />
              </div>
              <Form.Group className="mb-3">
                <Form.Label>Email</Form.Label>
                <Form.Control
                  value={values.email}
                  onChange={(e) => setFieldValue("email", e.target.value)}
                />
                {errors.email && (
                  <p className='text-field__error'>
                    {errors.email}
                  </p>
                )}
              </Form.Group>
            </div>
          }

          <Form.Group className="mb-3">
            <Form.Control
              value={details}
              as="textarea"
              rows={4}
              onChange={(e) => setDetails(e.target.value)}
              placeHolder="Anything you want to share in your application..."
            />
          </Form.Group>
          <div className='cta'>
            <Button className={classNames("ppy-btn", "ppy-btn-primary", {disabled: isLoading || (!currentUser && values.email === '')})} onClick={handleApply}>
              Apply <FontAwesomeIcon icon={faShare} />
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
}
